import { useEffect, useState } from "react";
import DashbordStyles from "./DashboardTabs.module.scss";
import CardDataRenderer from "../../../../components/CardDataRenderer/CardDataRenderer";
import { useAppDispatch, useAppSelector } from "../../../../hooks/store";
import {
  getClauseCheckThunk,
  getContractsReviewThunk,
} from "../../store/dashboard.thunk";
import Typography from "../../../../components/Typography/Typography";
import {
  DASHBOARD_FEATURES,
  dashboardConfig,
  LANDING_PAGE_TO_DASHBOARD_URLS,
} from "../../constants";
import { isEqual } from "underscore";
import VmoBot from "../vmoBot/VmoBot";
import { DASHBOARD_ENTITIES, ROUTES } from "../../../../common/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import UploadDoc from "../../../../components/UploadDoc/UploadDoc";
import BreathingDashboard from "../../components/BreathingDashboard/BreathingDashboard";
import { actionType } from "../../constants";
import DraftingDashboard from "../../components/DraftingDashboard/DraftingDashboard";
import RfxEvaluationDashboard from "../../components/RfxEvaluationDashboard/RfxEvaluationDashboard";
import RfxGeneratorDashboard from "../../components/RfxGeneratorDashboard/RfxGeneratorDashboard";
import { getFilteredDashboardFeatures } from "../../utils";
import { getProjectMappings } from "../../../../services/common.service";

const DashboardTabs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cardType = searchParams.get("cardType");
  const cardActionType = searchParams.get("actionType");

  const [openModal, setOpenModal] = useState(false);
  const { contractsReview, clauseCheck } = useAppSelector(
    (state) => state.rootReducer.vscDashboardReducer
  );

  const dispatch = useAppDispatch();

  const profileData = useAppSelector(
    (store) => store?.rootReducer?.userInfoReducer?.profileData?.data
  );
  const [selectedCard, setSelectedCard] = useState(
    dashboardConfig?.[DASHBOARD_FEATURES.TALK_TO_CONTRACT]
  );
  const isClauseCheck =
    cardType === LANDING_PAGE_TO_DASHBOARD_URLS.clause_check;
  const isContractDrafting =
    cardType === LANDING_PAGE_TO_DASHBOARD_URLS.contract_drafting;

  const isContracRedliningAllowed = profileData?.isContractReviewAllowed;
  const isClauseCheckAllowed = profileData?.isClauseCheckAllowed;
  const isRFxAllowed = isContracRedliningAllowed || isClauseCheckAllowed;
  const showContractRedlining =
    selectedCard.headerText === DASHBOARD_FEATURES.CONTRACT_REDLINING &&
    isClauseCheckAllowed;
  const showClauseCheck =
    selectedCard.headerText === DASHBOARD_FEATURES.CLAUSE_CHECK &&
    isClauseCheckAllowed;
  const showTalkToContract =
    selectedCard.headerText === DASHBOARD_FEATURES.TALK_TO_CONTRACT;
  // const showBreathingDashboard =
  //   selectedCard.headerText === DASHBOARD_FEATURES.BREATHING_DASHBOARD;
  const showDraftingDashboard =
    selectedCard.headerText === DASHBOARD_FEATURES.CONTRACT_DRAFTING &&
    isClauseCheckAllowed;
  const showRfxEvaluationDashboard =
    selectedCard.headerText === DASHBOARD_FEATURES.RFX_EVALUATION &&
    isRFxAllowed;
  const showRfxGeneratorDashboard =
    (selectedCard.headerText === DASHBOARD_FEATURES.RFX_GENERATOR ||
      selectedCard.headerText === DASHBOARD_FEATURES.REQUIREMENTS_GENERATOR) &&
    isRFxAllowed;

  const handleClick = (cardData: any) => {
    navigate(`${ROUTES.dashboardTabs}?cardType=${cardData?.featureUrl}`);
    setSelectedCard(cardData);
  };

  const handleTabClick = () => {
    switch (cardType) {
      case LANDING_PAGE_TO_DASHBOARD_URLS.ttc:
        setSelectedCard(dashboardConfig?.[DASHBOARD_FEATURES.TALK_TO_CONTRACT]);
        break;
      case LANDING_PAGE_TO_DASHBOARD_URLS.clause_check:
        if (cardActionType === actionType.UPLOAD) {
          setOpenModal(true);
        }
        setSelectedCard(dashboardConfig?.[DASHBOARD_FEATURES.CLAUSE_CHECK]);
        break;
      case LANDING_PAGE_TO_DASHBOARD_URLS.contract_redlining:
        if (cardActionType === actionType.UPLOAD) {
          setOpenModal(true);
        }
        setSelectedCard(
          dashboardConfig?.[DASHBOARD_FEATURES.CONTRACT_REDLINING]
        );
        break;
      // case LANDING_PAGE_TO_DASHBOARD_URLS.breathing_dashboard:
      //   setSelectedCard(
      //     dashboardConfig?.[DASHBOARD_FEATURES.BREATHING_DASHBOARD]
      //   );
      //   break;
      case LANDING_PAGE_TO_DASHBOARD_URLS.contract_drafting:
        setSelectedCard(
          dashboardConfig?.[DASHBOARD_FEATURES.CONTRACT_DRAFTING]
        );
        break;
      case LANDING_PAGE_TO_DASHBOARD_URLS.rfx_evaluation:
        setSelectedCard(dashboardConfig?.[DASHBOARD_FEATURES.RFX_EVALUATION]);
        break;
      case LANDING_PAGE_TO_DASHBOARD_URLS.rfx_generator:
        setSelectedCard(dashboardConfig?.[DASHBOARD_FEATURES.RFX_GENERATOR]);
        break;
      case "requirements_generator":
        setSelectedCard(dashboardConfig?.[DASHBOARD_FEATURES.REQUIREMENTS_GENERATOR]);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    handleTabClick();
  }, [cardActionType, cardType]);

  useEffect(() => {
    isClauseCheckAllowed && dispatch(getContractsReviewThunk());
    isClauseCheckAllowed && dispatch(getClauseCheckThunk({ polling: false }));
  }, [dispatch, isClauseCheckAllowed, isContracRedliningAllowed]);

  // Get project name for tab order logic
  const [projectName, setProjectName] = useState("");
  useEffect(() => {
    const fetchProjectName = async () => {
      try {
        const response = await getProjectMappings();
        if (
          response &&
          response.project_names &&
          response.project_names.length > 0
        ) {
          // Prefer "Orion" if present, else default to first project
          const orionProject = response.project_names.find(
            (name: string) => name === "Orion"
          );
          setProjectName(orionProject || response.project_names[0]);
        }
      } catch (error) {
        console.error("Error fetching project name:", error);
      }
    };
    fetchProjectName();
  }, []);

  let filteredDashboardFeatures = getFilteredDashboardFeatures(
    isClauseCheckAllowed,
    isContracRedliningAllowed
  );
  filteredDashboardFeatures = filteredDashboardFeatures.filter(
    (featureKey) => featureKey !== DASHBOARD_FEATURES.REQUIREMENTS_GENERATOR
  );
  if (projectName === "SourcingHub") {
    filteredDashboardFeatures.unshift(DASHBOARD_FEATURES.REQUIREMENTS_GENERATOR);
  }

  return (
    <div className={DashbordStyles.dashboardContainer}>
      {/* Removed Dashboard Selector */}
      <div className={DashbordStyles.mainContent}>
        <div className={DashbordStyles.leftContent}>
          {filteredDashboardFeatures.map((featureKey) => {
            const cardData = dashboardConfig?.[featureKey];
            return (
              <HeaderCard
                key={featureKey}
                cardData={cardData}
                handleClick={handleClick}
                selectedCard={selectedCard}
              />
            );
          })}
        </div>
        <div className={DashbordStyles.rightContent}>
          {showContractRedlining && <CardDataRenderer {...contractsReview} />}
          {showClauseCheck && <CardDataRenderer {...clauseCheck} />}
          {showTalkToContract && <VmoBot />}
          {/* {showBreathingDashboard && <BreathingDashboard />} */}
          {showDraftingDashboard && <DraftingDashboard {...contractsReview} />}
          {showRfxEvaluationDashboard && <RfxEvaluationDashboard />}
          {showRfxGeneratorDashboard && (
            <RfxGeneratorDashboard
              isRequirements={selectedCard.headerText === DASHBOARD_FEATURES.REQUIREMENTS_GENERATOR}
            />
          )}
        </div>
      </div>
      <UploadDoc
        openModal={openModal}
        setOpenModal={setOpenModal}
        dashboardEntity={
          isContractDrafting
            ? DASHBOARD_ENTITIES.CONTRACT_DRAFTING
            : isClauseCheck
            ? DASHBOARD_ENTITIES.CLAUSE_CHECK
            : DASHBOARD_ENTITIES.CONTRACT_REVIEW
        }
      />
    </div>
  );
};

const HeaderCard = ({ cardData, handleClick, selectedCard }: any) => {
  const { headerText, icon } = cardData;
  const isActive = isEqual(selectedCard, cardData);
  return (
    <div
      className={`${DashbordStyles.card} ${
        isActive && DashbordStyles.selectedCard
      }`}
      onClick={(e) => {
        e.stopPropagation();
        handleClick(cardData);
      }}
    >
      <div className={DashbordStyles.cardDescription}>
        <div className={DashbordStyles.cardIcon}>
          <img src={`${isActive ? icon.selected : icon.unselected}`} alt="" />
        </div>
        <div>
          <Typography
            fontSize={14}
            fontWeight={400}
            className={`${isActive && DashbordStyles.selectedCardText}`}
          >
            {headerText}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default DashboardTabs;
